<div class="vs bm" [class.is-dark]="isDarkMode()">
  <!-- ======================= Title + view switch + exports ======================= -->
  <header class="bm-head">
    <div class="bm-head__title">
      <span class="vs-eyebrow">Data analytics</span>
      <h1>Performance benchmarking</h1>
      <p>Voyage heating fuel against benchmark consumption, with fuel and CO₂ savings</p>
    </div>

    <div class="bm-head__actions">
      <div *ngIf="isAdminUser" class="vs-tabs" role="tablist" aria-label="View">
        <button type="button" role="tab" class="vs-tab" [class.is-active]="showGraphView"
          [attr.aria-selected]="showGraphView" (click)="!showGraphView && toggleGraphView()">
          <mat-icon>donut_large</mat-icon>
          <span>Summary</span>
        </button>
        <button type="button" role="tab" class="vs-tab" [class.is-active]="!showGraphView"
          [attr.aria-selected]="!showGraphView" (click)="showGraphView && toggleGraphView()">
          <mat-icon>table_rows</mat-icon>
          <span>Tabular</span>
        </button>
      </div>

      <button *ngIf="isAdminUser && !isBenchLoading" type="button" class="vs-btn" (click)="downloadCSV()"
        title="Export filtered records to CSV">
        <mat-icon>download</mat-icon>
        <span>CSV</span>
      </button>

      <!-- Wrapper carries the tooltip: a disabled button receives no pointer events -->
      <span class="bm-tip-wrap" [matTooltip]="benchmarkReportDisabledTooltip"
        [matTooltipDisabled]="canOpenBenchmarkReport" matTooltipPosition="below">
        <button type="button" class="vs-btn vs-btn--primary" [disabled]="!canOpenBenchmarkReport || openingBenchmarkReport"
          (click)="openBenchmarkReport()" title="Export Client Benchmark Report as PDF">
          <mat-icon [class.vs-spin]="openingBenchmarkReport">{{ openingBenchmarkReport ? 'autorenew' : 'picture_as_pdf'
            }}</mat-icon>
          <span>PDF report</span>
        </button>
      </span>
    </div>
  </header>

  <!-- ======================= KPI tiles ======================= -->
  <section class="bm-kpis" *ngIf="!isBenchLoading; else bmKpisLoading" aria-label="Benchmark summary">
    <div class="bm-kpi" *ngFor="let kpi of kpiTiles; trackBy: trackKpi" [ngClass]="'bm-kpi--' + kpi.theme">
      <div class="bm-kpi__top">
        <span class="bm-kpi__icon"><mat-icon>{{ kpi.icon }}</mat-icon></span>
        <span class="bm-kpi__label">{{ kpi.title }}</span>
      </div>
      <div class="bm-kpi__value">
        {{ kpi.value | number : kpi.format }}<small *ngIf="kpi.unit">{{ kpi.unit }}</small>
        <span class="bm-kpi__pct" *ngIf="kpi.savingPct != null"
          [title]="'Fuel saving ÷ benchmark consumption: ' + (kpi.savingPct | number : '1.0-2') + '%'">
          {{ kpi.savingPct | number : '1.0-1' }}%<em>of benchmark</em>
        </span>
      </div>
      <div class="bm-kpi__share" [title]="'Veg Oil ' + kpi.vegPct + '%  ·  DPP ' + kpi.dppPct + '%'" aria-hidden="true">
        <span class="is-veg" [style.width.%]="kpi.vegPct"></span>
        <span class="is-dpp" [style.width.%]="kpi.dppPct"></span>
      </div>
      <div class="bm-kpi__split">
        <span class="is-veg" [title]="kpi.title + ' — Veg Oil (' + kpi.vegPct + '%)'">
          <i></i>Veg Oil<b>{{ kpi.veg | number : kpi.format }}</b>
          <em *ngIf="kpi.vegSavingPct != null" title="Veg Oil fuel saving vs Veg Oil benchmark consumption">
            {{ kpi.vegSavingPct | number : '1.0-1' }}%</em>
        </span>
        <span class="is-dpp" [title]="kpi.title + ' — DPP (' + kpi.dppPct + '%)'">
          <i></i>DPP<b>{{ kpi.dpp | number : kpi.format }}</b>
          <em *ngIf="kpi.dppSavingPct != null" title="DPP fuel saving vs DPP benchmark consumption">
            {{ kpi.dppSavingPct | number : '1.0-1' }}%</em>
        </span>
      </div>
    </div>
  </section>
  <ng-template #bmKpisLoading>
    <div class="bm-kpis bm-kpis--loading"><app-ch-skeleton variant="tiles" [tiles]="4"></app-ch-skeleton></div>
  </ng-template>

  <!-- ======================= Workspace: filters + table / summary ======================= -->
  <section class="vs-card bm-card" [class.is-summary]="showGraphView" aria-label="Benchmark records">
    <div class="vs-toolbar">
      <!-- Filters (apply to both views) -->
      <div class="vs-toolbar__row vs-filters">
        <span class="vs-filters__label"><mat-icon>tune</mat-icon>Filters</span>

        <!-- Client -->
        <div class="vs-ms" *ngIf="isAdminUser">
          <button type="button" class="vs-ms__box" [class.has-selection]="selectedClients.length > 0"
            [class.is-open]="clientDropdownOpen" (click)="toggleClientDropdown($event)"
            [attr.aria-expanded]="clientDropdownOpen">
            <mat-icon class="vs-ms__icon">corporate_fare</mat-icon>
            <span class="vs-ms__label" [title]="selectedClients.join(', ')">{{ selectedClients.length ?
              (selectedClients.length === clientNames.length ? 'All clients'
              : (selectedClients.length === 1 ? selectedClients[0] : selectedClients.length + ' clients')) :
              'Client' }}</span>
            <span class="vs-ms__count"
              *ngIf="selectedClients.length > 0 && selectedClients.length < clientNames.length">{{
              selectedClients.length }}</span>
            <mat-icon class="vs-ms__clear" *ngIf="selectedClients.length > 0"
              (click)="clearSingleFilter('client', $event)" title="Clear client filter">close</mat-icon>
            <mat-icon class="vs-ms__arrow">expand_more</mat-icon>
          </button>
          <div class="vs-ms__panel" *ngIf="clientDropdownOpen" (click)="$event.stopPropagation()">
            <div class="vs-ms__search">
              <mat-icon>search</mat-icon>
              <input type="text" placeholder="Search client…" (input)="clientSearchQuery = $any($event.target).value"
                [value]="clientSearchQuery" />
              <mat-icon class="vs-ms__search-clear" *ngIf="clientSearchQuery"
                (click)="clientSearchQuery = ''">close</mat-icon>
            </div>
            <div class="vs-ms__all">
              <label class="vs-check">
                <input type="checkbox" (change)="toggleAllClients($event)" [checked]="isAllClientsSelected()" />
                <span class="vs-check__box"></span>
                <span>Select all</span>
              </label>
              <span class="vs-ms__status">{{ selectedClients.length }} of {{ clientNames.length }}</span>
            </div>
            <div class="vs-ms__list">
              <label class="vs-check vs-ms__option" *ngFor="let client of getFilteredClients()"
                [class.is-selected]="selectedClients.includes(client)">
                <input type="checkbox" (change)="onClientChange($event, client)"
                  [checked]="selectedClients.includes(client)" />
                <span class="vs-check__box"></span>
                <span class="vs-ms__name">{{ client }}</span>
              </label>
              <div class="vs-ms__empty" *ngIf="getFilteredClients().length === 0">
                <mat-icon>search_off</mat-icon>No clients found
              </div>
            </div>
          </div>
        </div>

        <!-- Cargo grade (locked to Veg Oil for Bunge) -->
        <div class="vs-ms">
          <button type="button" class="vs-ms__box" [class.is-locked]="isBungeUser"
            [class.has-selection]="selectedCargoTypes.length > 0 && !isBungeUser" [class.is-open]="cargoDropdownOpen"
            (click)="toggleCargoDropdown($event)" [attr.aria-expanded]="cargoDropdownOpen"
            [attr.aria-disabled]="isBungeUser" [matTooltip]="isBungeUser ? 'Locked to Veg Oil for Bunge' : ''"
            matTooltipPosition="above">
            <mat-icon class="vs-ms__icon">opacity</mat-icon>
            <span class="vs-ms__label" *ngIf="isBungeUser">Veg Oil</span>
            <span class="vs-ms__label" *ngIf="!isBungeUser" [title]="selectedCargoTypes.join(', ')">{{
              selectedCargoTypes.length ?
              (selectedCargoTypes.length === cargoTypes.length ? 'All cargo'
              : (selectedCargoTypes.length === 1 ? selectedCargoTypes[0] : selectedCargoTypes.length + ' cargo')) :
              'Cargo grade' }}</span>
            <span class="vs-ms__count"
              *ngIf="selectedCargoTypes.length > 0 && selectedCargoTypes.length < cargoTypes.length && !isBungeUser">{{
              selectedCargoTypes.length }}</span>
            <mat-icon class="vs-ms__clear" *ngIf="selectedCargoTypes.length > 0 && !isBungeUser"
              (click)="clearSingleFilter('cargo', $event)" title="Clear cargo filter">close</mat-icon>
            <mat-icon class="vs-ms__arrow" *ngIf="!isBungeUser">expand_more</mat-icon>
            <mat-icon class="vs-ms__arrow" *ngIf="isBungeUser">lock</mat-icon>
          </button>
          <div class="vs-ms__panel" *ngIf="cargoDropdownOpen && !isBungeUser" (click)="$event.stopPropagation()">
            <div class="vs-ms__search">
              <mat-icon>search</mat-icon>
              <input type="text" placeholder="Search cargo…" (input)="cargoSearchQuery = $any($event.target).value"
                [value]="cargoSearchQuery" />
              <mat-icon class="vs-ms__search-clear" *ngIf="cargoSearchQuery"
                (click)="cargoSearchQuery = ''">close</mat-icon>
            </div>
            <div class="vs-ms__all">
              <label class="vs-check">
                <input type="checkbox" (change)="toggleAllCargos($event)" [checked]="isAllCargosSelected()" />
                <span class="vs-check__box"></span>
                <span>Select all</span>
              </label>
              <span class="vs-ms__status">{{ selectedCargoTypes.length }} of {{ cargoTypes.length }}</span>
            </div>
            <div class="vs-ms__list">
              <label class="vs-check vs-ms__option" *ngFor="let cargo of getFilteredCargos()"
                [class.is-selected]="selectedCargoTypes.includes(cargo)">
                <input type="checkbox" (change)="onCargoChange($event, cargo)"
                  [checked]="selectedCargoTypes.includes(cargo)" />
                <span class="vs-check__box"></span>
                <span class="vs-ms__name">{{ cargo }}</span>
              </label>
              <div class="vs-ms__empty" *ngIf="getFilteredCargos().length === 0">
                <mat-icon>search_off</mat-icon>No cargo grades found
              </div>
            </div>
          </div>
        </div>

        <!-- Vessel -->
        <div class="vs-ms">
          <button type="button" class="vs-ms__box" [class.has-selection]="selectedVessels.length > 0"
            [class.is-open]="vesselDropdownOpen" (click)="toggleVesselDropdown($event)"
            [attr.aria-expanded]="vesselDropdownOpen">
            <mat-icon class="vs-ms__icon">directions_boat</mat-icon>
            <span class="vs-ms__label" [title]="selectedVessels.join(', ')">{{ selectedVessels.length ?
              (selectedVessels.length === vesselNames.length ? 'All vessels'
              : (selectedVessels.length === 1 ? selectedVessels[0] : selectedVessels.length + ' vessels')) :
              'Vessel' }}</span>
            <span class="vs-ms__count"
              *ngIf="selectedVessels.length > 0 && selectedVessels.length < vesselNames.length">{{
              selectedVessels.length }}</span>
            <mat-icon class="vs-ms__clear" *ngIf="selectedVessels.length > 0"
              (click)="clearSingleFilter('vessel', $event)" title="Clear vessel filter">close</mat-icon>
            <mat-icon class="vs-ms__arrow">expand_more</mat-icon>
          </button>
          <div class="vs-ms__panel" *ngIf="vesselDropdownOpen" (click)="$event.stopPropagation()">
            <div class="vs-ms__search">
              <mat-icon>search</mat-icon>
              <input type="text" placeholder="Search vessel…" (input)="vesselSearchQuery = $any($event.target).value"
                [value]="vesselSearchQuery" />
              <mat-icon class="vs-ms__search-clear" *ngIf="vesselSearchQuery"
                (click)="vesselSearchQuery = ''">close</mat-icon>
            </div>
            <div class="vs-ms__all">
              <label class="vs-check">
                <input type="checkbox" (change)="toggleAllVessels($event)" [checked]="isAllVesselsSelected()" />
                <span class="vs-check__box"></span>
                <span>Select all</span>
              </label>
              <span class="vs-ms__status">{{ selectedVessels.length }} of {{ vesselNames.length }}</span>
            </div>
            <div class="vs-ms__list">
              <label class="vs-check vs-ms__option" *ngFor="let vessel of getFilteredVessels()"
                [class.is-selected]="selectedVessels.includes(vessel)">
                <input type="checkbox" (change)="onVesselChange($event, vessel)"
                  [checked]="selectedVessels.includes(vessel)" />
                <span class="vs-check__box"></span>
                <span class="vs-ms__name">{{ vessel }}</span>
              </label>
              <div class="vs-ms__empty" *ngIf="getFilteredVessels().length === 0">
                <mat-icon>search_off</mat-icon>No vessels found
              </div>
            </div>
          </div>
        </div>

        <!-- Segment -->
        <div class="vs-ms">
          <button type="button" class="vs-ms__box" [class.has-selection]="selectedSegments.length > 0"
            [class.is-open]="segmentDropdownOpen" (click)="toggleSegmentDropdown($event)"
            [attr.aria-expanded]="segmentDropdownOpen">
            <mat-icon class="vs-ms__icon">category</mat-icon>
            <span class="vs-ms__label" [title]="selectedSegments.join(', ')">{{ selectedSegments.length ?
              (selectedSegments.length === segmentNames.length ? 'All segments'
              : (selectedSegments.length === 1 ? selectedSegments[0] : selectedSegments.length + ' segments')) :
              'Segment' }}</span>
            <span class="vs-ms__count"
              *ngIf="selectedSegments.length > 0 && selectedSegments.length < segmentNames.length">{{
              selectedSegments.length }}</span>
            <mat-icon class="vs-ms__clear" *ngIf="selectedSegments.length > 0"
              (click)="clearSingleFilter('segment', $event)" title="Clear segment filter">close</mat-icon>
            <mat-icon class="vs-ms__arrow">expand_more</mat-icon>
          </button>
          <div class="vs-ms__panel" *ngIf="segmentDropdownOpen" (click)="$event.stopPropagation()">
            <div class="vs-ms__search">
              <mat-icon>search</mat-icon>
              <input type="text" placeholder="Search segment…" (input)="segmentSearchQuery = $any($event.target).value"
                [value]="segmentSearchQuery" />
              <mat-icon class="vs-ms__search-clear" *ngIf="segmentSearchQuery"
                (click)="segmentSearchQuery = ''">close</mat-icon>
            </div>
            <div class="vs-ms__all">
              <label class="vs-check">
                <input type="checkbox" (change)="toggleAllSegments($event)" [checked]="isAllSegmentsSelected()" />
                <span class="vs-check__box"></span>
                <span>Select all</span>
              </label>
              <span class="vs-ms__status">{{ selectedSegments.length }} of {{ segmentNames.length }}</span>
            </div>
            <div class="vs-ms__list">
              <label class="vs-check vs-ms__option" *ngFor="let segment of getFilteredSegments()"
                [class.is-selected]="selectedSegments.includes(segment)">
                <input type="checkbox" (change)="onSegmentChange($event, segment)"
                  [checked]="selectedSegments.includes(segment)" />
                <span class="vs-check__box"></span>
                <span class="vs-ms__name">{{ segment }}</span>
              </label>
              <div class="vs-ms__empty" *ngIf="getFilteredSegments().length === 0">
                <mat-icon>search_off</mat-icon>No segments found
              </div>
            </div>
          </div>
        </div>

        <!-- Voyage end date range -->
        <div class="vs-date"
          [class.has-value]="benchamrkForm.controls['from_date'].value || benchamrkForm.controls['to_date'].value"
          (click)="rangePicker.open()">
          <mat-icon class="vs-date__icon">calendar_month</mat-icon>
          <span class="vs-date__label">Voyage end</span>
          <mat-date-range-input [formGroup]="benchamrkForm" [rangePicker]="rangePicker" class="vs-date__input"
            separator="→">
            <input matStartDate formControlName="from_date" placeholder="From" readonly (click)="rangePicker.open()"
              (dateChange)="onDateChange()" aria-label="Voyage end from" />
            <input matEndDate formControlName="to_date" placeholder="To" readonly (click)="rangePicker.open()"
              (dateChange)="onDateChange()" aria-label="Voyage end to" />
          </mat-date-range-input>
          <mat-icon class="vs-date__clear"
            *ngIf="benchamrkForm.controls['from_date'].value || benchamrkForm.controls['to_date'].value"
            (click)="clearDateFilter($event)" title="Clear date range">close</mat-icon>
          <mat-icon class="vs-date__info" (click)="$event.stopPropagation()"
            matTooltip="This date range filter is applied along the Vessel End Date"
            matTooltipClass="custom-modern-tooltip" matTooltipPosition="above">info_outline</mat-icon>
          <mat-datepicker-toggle matIconSuffix [for]="rangePicker"
            (click)="$event.stopPropagation()"></mat-datepicker-toggle>
          <mat-date-range-picker #rangePicker
            [panelClass]="isDarkMode() ? ['vs-datepicker', 'vs-datepicker--dark'] : ['vs-datepicker']">
            <mat-date-range-picker-actions>
              <button type="button" class="vs-dp-btn" matDateRangePickerCancel>Cancel</button>
              <button type="button" class="vs-dp-btn vs-dp-btn--primary" matDateRangePickerApply
                (click)="onDateChange()">Apply</button>
            </mat-date-range-picker-actions>
          </mat-date-range-picker>
        </div>

        <button type="button" class="vs-icon-btn" [class.has-active]="hasActiveFilters" (click)="clearClick()"
          title="Reset all filters" aria-label="Reset all filters">
          <mat-icon>restart_alt</mat-icon>
        </button>
      </div>

      <!-- Table tools: search, column sets, remarks -->
      <div class="vs-toolbar__row bm-tools" *ngIf="!showGraphView">
        <label class="vs-search">
          <mat-icon>search</mat-icon>
          <input type="text" placeholder="Search vessel, segment, port, grade, client…" [(ngModel)]="tableSearchQuery"
            (input)="onTableSearchChange()" aria-label="Search benchmark records" />
          <button type="button" class="vs-search__clear" *ngIf="tableSearchQuery"
            (click)="tableSearchQuery = ''; onTableSearchChange()" aria-label="Clear search" title="Clear search">
            <mat-icon>close</mat-icon>
          </button>
        </label>

        <div class="bm-columns">
          <span class="bm-columns__label">Columns</span>
          <div class="vs-tabs bm-columns__tabs" role="group" aria-label="Column set">
            <button type="button" class="vs-tab" [class.is-active]="activePreset === 'compact'"
              [attr.aria-pressed]="activePreset === 'compact'" (click)="setPresetView('compact')"
              title="Voyage, cargo, totals and savings">Compact</button>
            <button type="button" class="vs-tab" [class.is-active]="activePreset === 'days'"
              [attr.aria-pressed]="activePreset === 'days'" (click)="setPresetView('days')"
              title="Add load, sea and discharge days">+ Days</button>
            <button type="button" class="vs-tab" [class.is-active]="activePreset === 'consumption'"
              [attr.aria-pressed]="activePreset === 'consumption'" (click)="setPresetView('consumption')"
              title="Add FO / GO consumption by phase">+ Fuel breakdown</button>
            <button type="button" class="vs-tab" [class.is-active]="activePreset === 'all'"
              [attr.aria-pressed]="activePreset === 'all'" (click)="setPresetView('all')"
              title="Every metric">Full metrics</button>
          </div>
        </div>

        <div class="vs-toolbar__end">
          <button type="button" class="vs-btn bm-remarks-filter" [class.is-active]="showOnlyCommentedRecords"
            [attr.aria-pressed]="showOnlyCommentedRecords"
            [disabled]="commentedRecordsCount === 0 && !showOnlyCommentedRecords" (click)="toggleOnlyCommentedRecords()"
            [matTooltip]="showOnlyCommentedRecords ? 'Showing only records with comments. Click to show all.' : (commentedRecordsCount > 0 ? 'Show only records with operator remarks (' + commentedRecordsCount + ')' : 'No operator remarks recorded in filtered records')"
            matTooltipPosition="above">
            <mat-icon>{{ showOnlyCommentedRecords ? 'chat' : 'chat_bubble_outline' }}</mat-icon>
            <span>With remarks</span>
            <span class="vs-tab__count" *ngIf="commentedRecordsCount > 0">{{ commentedRecordsCount }}</span>
          </button>
          <span class="vs-records" *ngIf="!isBenchLoading">
            <b>{{ benchmarkData.length | number }}</b> record{{ benchmarkData.length === 1 ? '' : 's' }}
          </span>
        </div>
      </div>
    </div>

    <!-- ======================= Tabular view ======================= -->
    <div class="vs-table-body" *ngIf="!showGraphView">
      <div class="vs-table-loading" *ngIf="isBenchLoading">
        <app-ch-skeleton variant="table" [rows]="8" [cols]="10"></app-ch-skeleton>
      </div>

      <div class="vs-scroll" *ngIf="!isBenchLoading">
        <table class="vs-table bm-table" matSort matSortDisableClear [matSortActive]="currentSort.active"
          [matSortDirection]="currentSort.direction" (matSortChange)="sortData($event)">
          <colgroup>
            <col style="width: 72px" />
            <col style="width: 210px" />
            <col style="width: 116px" />
            <col style="width: 150px" *ngIf="isAdminUser" />
            <col style="width: 120px" />
            <col style="width: 200px" />
            <col style="width: 124px" />
            <col style="width: 200px" />
            <col style="width: 124px" />
            <col style="width: 124px" />
            <col style="width: 116px" />
            <col style="width: 96px" />
            <col style="width: 96px" />
            <ng-container *ngIf="showDaysColumns">
              <col style="width: 96px" />
              <col style="width: 96px" />
              <col style="width: 96px" />
            </ng-container>
            <col style="width: 100px" />
            <ng-container *ngIf="showConsumptionColumns">
              <col style="width: 96px" />
              <col style="width: 96px" />
              <col style="width: 96px" />
              <col style="width: 96px" />
              <col style="width: 96px" />
              <col style="width: 96px" />
            </ng-container>
            <col style="width: 124px" />
            <col style="width: 124px" />
            <col style="width: 132px" />
            <col style="width: 132px" />
          </colgroup>

          <thead>
            <!-- Column groups -->
            <tr class="bm-group-row">
              <th class="vs-col-sn" rowspan="2" scope="col">#</th>
              <th class="vs-col-vessel" rowspan="2" scope="col" mat-sort-header="vessel_name">Vessel</th>
              <th class="bm-group" scope="colgroup" [attr.colspan]="isAdminUser ? 7 : 6">Voyage</th>
              <th class="bm-group" scope="colgroup" colspan="4">Cargo</th>
              <th class="bm-group" scope="colgroup" [attr.colspan]="showDaysColumns ? 4 : 1">Days</th>
              <th class="bm-group" scope="colgroup" [attr.colspan]="showConsumptionColumns ? 8 : 2">
                Fuel consumption <small>MT</small>
              </th>
              <th class="bm-group bm-group--saving" scope="colgroup" colspan="2">Savings <small>MT</small></th>
            </tr>
            <tr class="bm-head-row">
              <th scope="col" mat-sort-header="segment" class="bm-group-start">Segment</th>
              <th scope="col" mat-sort-header="client_name" *ngIf="isAdminUser">Client</th>
              <th scope="col">Voy no</th>
              <th scope="col">Load port</th>
              <th scope="col" mat-sort-header="start_date">Load comp.</th>
              <th scope="col">Disch. port</th>
              <th scope="col" mat-sort-header="end_date">Disch. comp.</th>

              <th scope="col" mat-sort-header="grade" class="bm-group-start">Grade</th>
              <th scope="col" mat-sort-header="qty_mt" class="is-num">Qty (MT)</th>
              <th scope="col" class="is-num">Load °C</th>
              <th scope="col" class="is-num">Disch. °C</th>

              <ng-container *ngIf="showDaysColumns">
                <th scope="col" class="is-num bm-group-start">Load</th>
                <th scope="col" class="is-num">Sea</th>
                <th scope="col" class="is-num">Disch.</th>
              </ng-container>
              <th scope="col" class="is-num" [class.bm-group-start]="!showDaysColumns">Total</th>

              <ng-container *ngIf="showConsumptionColumns">
                <th scope="col" class="is-num bm-group-start">L port FO</th>
                <th scope="col" class="is-num">L port GO</th>
                <th scope="col" class="is-num">Sea FO</th>
                <th scope="col" class="is-num">Sea GO</th>
                <th scope="col" class="is-num">D port FO</th>
                <th scope="col" class="is-num">D port GO</th>
              </ng-container>
              <th scope="col" mat-sort-header="total_fuel_voy" class="is-num"
                [class.bm-group-start]="!showConsumptionColumns">Voyage</th>
              <th scope="col" mat-sort-header="benchmark_consp" class="is-num">Benchmark</th>

              <th scope="col" mat-sort-header="fuel_saving" class="is-num bm-group-start">Fuel</th>
              <th scope="col" mat-sort-header="co2_saving" class="is-num">CO₂</th>
            </tr>
          </thead>

          <tbody>
            <tr *ngFor="let element of paginatedData; let i = index" class="vs-row bm-row">
              <td class="vs-col-sn"><span class="vs-sn">{{ currentPage * itemsPerPage + i + 1 }}</span></td>

              <td class="vs-col-vessel">
                <div class="bm-vessel">
                  <span class="vs-vessel">
                    <span class="vs-vessel__avatar" aria-hidden="true">{{ (element.vessel_name || '?').charAt(0)
                      }}</span>
                    <span class="vs-vessel__name" [matTooltip]="element.vessel_name" matTooltipPosition="above">{{
                      element.vessel_name }}</span>
                  </span>
                  <button type="button" class="bm-remarks-btn" [class.has-remarks]="!!getOperatorRemarks(element)"
                    (click)="openOperatorRemarksPopup(element)" [matTooltip]="getOperatorRemarksTooltip(element)"
                    matTooltipPosition="above" matTooltipClass="vm-operator-remarks-tooltip"
                    [attr.aria-label]="getOperatorRemarksTooltip(element)">
                    <mat-icon>{{ getOperatorRemarks(element) ? 'chat' : 'add_comment' }}</mat-icon>
                  </button>
                </div>
              </td>

              <td class="bm-group-start">
                <span class="bm-segment" *ngIf="element.segment; else bmDash" [title]="element.segment">{{
                  element.segment }}</span>
              </td>
              <td *ngIf="isAdminUser" [matTooltip]="element.client_name" matTooltipPosition="above">
                <span *ngIf="element.client_name; else bmDash">{{ element.client_name }}</span>
              </td>
              <td>
                <span class="bm-voy" *ngIf="element.voy_no; else bmDash">{{ element.voy_no }}</span>
              </td>
              <td [matTooltip]="element.start_port" matTooltipPosition="above">
                <span *ngIf="element.start_port; else bmDash">{{ element.start_port }}</span>
              </td>
              <td class="vs-num"><span *ngIf="element.start_date; else bmDash">{{ element.start_date }}</span></td>
              <td [matTooltip]="element.end_port" matTooltipPosition="above">
                <span *ngIf="element.end_port; else bmDash">{{ element.end_port }}</span>
              </td>
              <td class="vs-num"><span *ngIf="element.end_date; else bmDash">{{ element.end_date }}</span></td>

              <td class="bm-group-start">
                <span class="bm-grade" *ngIf="element.grade; else bmDash" [class.is-veg]="isVegOil(element)"
                  [title]="element.cargo_type || ''">{{ element.grade }}</span>
              </td>
              <td class="is-num bm-strong">
                <span *ngIf="element.qty_mt; else bmDash">{{ element.qty_mt | number: '1.0-2' }}</span>
              </td>
              <td class="is-num">
                <span *ngIf="element.load_temp != null; else bmDash">{{ element.load_temp }}<small
                    class="bm-unit">°C</small></span>
              </td>
              <td class="is-num">
                <span *ngIf="element.discharge_temp != null; else bmDash">{{ element.discharge_temp }}<small
                    class="bm-unit">°C</small></span>
              </td>

              <ng-container *ngIf="showDaysColumns">
                <td class="is-num bm-soft bm-group-start">{{ element.days_load_port ?? '—' }}</td>
                <td class="is-num bm-soft">{{ element.days_at_sea ?? '—' }}</td>
                <td class="is-num bm-soft">{{ element.days_disch_port ?? '—' }}</td>
              </ng-container>
              <td class="is-num bm-strong" [class.bm-group-start]="!showDaysColumns">{{ element.days_total ?? '—' }}
              </td>

              <ng-container *ngIf="showConsumptionColumns">
                <td class="is-num bm-soft bm-group-start">{{ element.l_port_fo ?? '—' }}</td>
                <td class="is-num bm-soft">{{ element.l_port_go ?? '—' }}</td>
                <td class="is-num bm-soft">{{ element.at_sea_fo ?? '—' }}</td>
                <td class="is-num bm-soft">{{ element.at_sea_go ?? '—' }}</td>
                <td class="is-num bm-soft">{{ element.d_port_fo ?? '—' }}</td>
                <td class="is-num bm-soft">{{ element.d_port_go ?? '—' }}</td>
              </ng-container>
              <td class="is-num bm-strong" [class.bm-group-start]="!showConsumptionColumns">{{ element.total_fuel_voy ??
                '—' }}</td>
              <td class="is-num bm-strong">{{ element.benchmark_consp ?? '—' }}</td>

              <!-- Fuel saving -->
              <td class="is-num bm-group-start">
                <span class="bm-delta" [class.is-pos]="element.fuel_saving > 0" [class.is-neg]="element.fuel_saving < 0">
                  <mat-icon *ngIf="element.fuel_saving > 0">trending_down</mat-icon>
                  <mat-icon *ngIf="element.fuel_saving < 0">trending_up</mat-icon>
                  {{ element.fuel_saving ? (element.fuel_saving > 0 ? '+' : '') + (element.fuel_saving | number: '1.0-2')
                  : '0' }}
                </span>
              </td>

              <!-- CO2 saving -->
              <td class="is-num">
                <span class="bm-delta" [class.is-pos]="element.co2_saving > 0" [class.is-neg]="element.co2_saving < 0">
                  <mat-icon *ngIf="element.co2_saving > 0">eco</mat-icon>
                  <mat-icon *ngIf="element.co2_saving < 0">warning_amber</mat-icon>
                  {{ element.co2_saving ? (element.co2_saving > 0 ? '+' : '') + (element.co2_saving | number: '1.0-2') :
                  '0' }}
                </span>
              </td>
            </tr>
          </tbody>

          <!-- Totals across every filtered record (not just this page) -->
          <tfoot *ngIf="benchmarkData.length > 0">
            <tr class="bm-total-row">
              <td class="vs-col-sn"><mat-icon class="bm-total-icon">functions</mat-icon></td>
              <td class="vs-col-vessel">
                Total <span class="bm-total-sub">{{ benchmarkData.length | number }} records</span>
              </td>
              <td [attr.colspan]="isAdminUser ? 8 : 7" class="bm-group-start"></td>
              <td class="is-num" [title]="'Total cargo: ' + (totalCargoVolumeMt | number: '1.0-2') + ' MT'">
                {{ totalCargoVolumeMt | number: '1.0-0' }}</td>
              <td colspan="2"></td>
              <td [attr.colspan]="showDaysColumns ? 4 : 1" class="bm-group-start"></td>
              <td *ngIf="showConsumptionColumns" colspan="6" class="bm-group-start"></td>
              <td class="is-num" [class.bm-group-start]="!showConsumptionColumns"
                [title]="'Total Voy Consp: ' + (totalVoyageConsumption | number: '1.0-2') + ' MT'">
                {{ totalVoyageConsumption | number: '1.0-1' }}</td>
              <td class="is-num" [title]="'Total Bench Consp: ' + (totalBenchmarkConsumption | number: '1.0-2') + ' MT'">
                {{ totalBenchmarkConsumption | number: '1.0-1' }}</td>
              <td class="is-num bm-group-start">
                <span class="bm-delta" [class.is-pos]="totalFuelSavingResult > 0"
                  [class.is-neg]="totalFuelSavingResult < 0">{{ totalFuelSavingResult | number: '1.0-1' }}</span>
              </td>
              <td class="is-num">
                <span class="bm-delta" [class.is-pos]="totalCo2SavingResult > 0"
                  [class.is-neg]="totalCo2SavingResult < 0">{{ totalCo2SavingResult | number: '1.0-1' }}</span>
              </td>
            </tr>
          </tfoot>
        </table>

        <div class="vs-no-rows" *ngIf="paginatedData.length === 0">
          <span class="vs-no-rows__icon"><mat-icon>{{ showOnlyCommentedRecords ? 'chat_bubble_outline' :
              'filter_alt_off' }}</mat-icon></span>
          <h3 *ngIf="!showOnlyCommentedRecords">No matching benchmark records</h3>
          <h3 *ngIf="showOnlyCommentedRecords">No records with operator remarks</h3>
          <p *ngIf="!showOnlyCommentedRecords">Try adjusting the filters or the search query.</p>
          <p *ngIf="showOnlyCommentedRecords">No benchmark records with operator remarks match the current criteria.</p>
          <button type="button" class="vs-btn" *ngIf="showOnlyCommentedRecords" (click)="toggleOnlyCommentedRecords()">
            <mat-icon>refresh</mat-icon>
            <span>Show all records</span>
          </button>
        </div>
      </div>
    </div>

    <footer class="vs-table-foot" *ngIf="!showGraphView && !isBenchLoading">
      <span class="bm-legend" aria-label="Legend">
        <span class="bm-legend__item"><i class="is-veg"></i>Veg Oil</span>
        <span class="bm-legend__item"><i class="is-dpp"></i>DPP</span>
        <span class="bm-legend__sep"></span>
        <span class="bm-legend__item is-pos"><mat-icon>trending_down</mat-icon>Saving vs benchmark</span>
        <span class="bm-legend__item is-neg"><mat-icon>trending_up</mat-icon>Above benchmark</span>
      </span>
      <mat-paginator class="vs-paginator" [length]="benchmarkData.length" [pageSize]="itemsPerPage"
        [pageIndex]="currentPage" [pageSizeOptions]="[10, 15, 25, 50]" (page)="onPageChange($event)"
        showFirstLastButtons>
      </mat-paginator>
    </footer>

    <!-- ======================= Summary view ======================= -->
    <div class="bm-summary" *ngIf="showGraphView">
      <div class="bm-panels" *ngIf="isBenchLoading">
        <div class="bm-panel bm-panel--loading"><app-ch-skeleton variant="chart"></app-ch-skeleton></div>
        <div class="bm-panel bm-panel--loading"><app-ch-skeleton variant="chart"></app-ch-skeleton></div>
        <div class="bm-panel bm-panel--loading"><app-ch-skeleton variant="chart"></app-ch-skeleton></div>
      </div>

      <ng-container *ngIf="isAdminUser && !isBenchLoading">
        <ng-container *ngIf="benchmarkData.length > 0">
          <div class="bm-panels">
            <!-- Voyage portfolio -->
            <article class="bm-panel bm-panel--voyages">
              <header class="bm-panel__head">
                <span class="bm-panel__icon"><mat-icon>directions_boat</mat-icon></span>
                <div class="bm-panel__titles">
                  <h3>Voyage portfolio</h3>
                  <p>Voyages by client account</p>
                </div>
              </header>
              <div class="bm-donut">
                <div [chart]="voyageChart" class="bm-donut__chart"></div>
                <div class="bm-donut__center" aria-hidden="true">
                  <b>{{ totalVoyagesSummary | number }}</b>
                  <span>Voyages</span>
                </div>
              </div>
              <div class="bm-breakdown">
                <div class="bm-breakdown__head">
                  <span>Client</span>
                  <span>{{ voyageBreakdownList.length }} accounts</span>
                </div>
                <div class="bm-breakdown__list" #voyageScrollContainer>
                  <div class="bm-breakdown__item" *ngFor="let item of voyageBreakdownList; trackBy: trackBreakdown">
                    <span class="bm-breakdown__dot" [style.background-color]="item.color"></span>
                    <span class="bm-breakdown__name" [title]="item.name">{{ item.name }}</span>
                    <span class="bm-breakdown__pct">{{ item.percentage | number:'1.1-1' }}%</span>
                    <span class="bm-breakdown__val">{{ item.value | number }} <small>voy</small></span>
                    <span class="bm-breakdown__bar"><span [style.width.%]="item.percentage"
                        [style.background-color]="item.color"></span></span>
                  </div>
                </div>
              </div>
            </article>

            <!-- Fuel savings -->
            <article class="bm-panel bm-panel--fuel">
              <header class="bm-panel__head">
                <span class="bm-panel__icon"><mat-icon>local_fire_department</mat-icon></span>
                <div class="bm-panel__titles">
                  <h3>Fuel savings</h3>
                  <p>Heating fuel saved against benchmark</p>
                </div>
              </header>
              <div class="bm-donut">
                <div [chart]="fuelSavingChart" class="bm-donut__chart"></div>
                <div class="bm-donut__center" aria-hidden="true">
                  <b>{{ totalFuelSummary | number:'1.0-0' }}</b>
                  <span>MT saved</span>
                </div>
              </div>
              <div class="bm-breakdown">
                <div class="bm-breakdown__head">
                  <span>Client</span>
                  <span>{{ fuelBreakdownList.length }} accounts</span>
                </div>
                <div class="bm-breakdown__list" #fuelScrollContainer>
                  <div class="bm-breakdown__item" *ngFor="let item of fuelBreakdownList; trackBy: trackBreakdown">
                    <span class="bm-breakdown__dot" [style.background-color]="item.color"></span>
                    <span class="bm-breakdown__name" [title]="item.name">{{ item.name }}</span>
                    <span class="bm-breakdown__pct">{{ item.percentage | number:'1.1-1' }}%</span>
                    <span class="bm-breakdown__val" [class.is-neg]="item.value < 0">{{ item.value | number:'1.0-1' }}
                      <small>MT</small></span>
                    <span class="bm-breakdown__bar"><span [style.width.%]="item.percentage"
                        [style.background-color]="item.color"></span></span>
                  </div>
                </div>
              </div>
            </article>

            <!-- CO2 reduction -->
            <article class="bm-panel bm-panel--co2">
              <header class="bm-panel__head">
                <span class="bm-panel__icon"><mat-icon>eco</mat-icon></span>
                <div class="bm-panel__titles">
                  <h3>CO₂ reduction</h3>
                  <p>Emissions avoided by heating efficiency</p>
                </div>
              </header>
              <div class="bm-donut">
                <div [chart]="co2SavingChart" class="bm-donut__chart"></div>
                <div class="bm-donut__center" aria-hidden="true">
                  <b>{{ totalCo2Summary | number:'1.0-0' }}</b>
                  <span>MT CO₂</span>
                </div>
              </div>
              <div class="bm-breakdown">
                <div class="bm-breakdown__head">
                  <span>Client</span>
                  <span>{{ co2BreakdownList.length }} accounts</span>
                </div>
                <div class="bm-breakdown__list" #co2ScrollContainer>
                  <div class="bm-breakdown__item" *ngFor="let item of co2BreakdownList; trackBy: trackBreakdown">
                    <span class="bm-breakdown__dot" [style.background-color]="item.color"></span>
                    <span class="bm-breakdown__name" [title]="item.name">{{ item.name }}</span>
                    <span class="bm-breakdown__pct">{{ item.percentage | number:'1.1-1' }}%</span>
                    <span class="bm-breakdown__val" [class.is-neg]="item.value < 0">{{ item.value | number:'1.0-1' }}
                      <small>MT</small></span>
                    <span class="bm-breakdown__bar"><span [style.width.%]="item.percentage"
                        [style.background-color]="item.color"></span></span>
                  </div>
                </div>
              </div>
            </article>
          </div>

          <!-- Actual vs benchmark consumption -->
          <article class="bm-panel bm-panel--wide">
            <header class="bm-panel__head">
              <span class="bm-panel__icon"><mat-icon>compare_arrows</mat-icon></span>
              <div class="bm-panel__titles">
                <h3>Voyage vs benchmark consumption</h3>
                <p>Total heating fuel per client account, MT</p>
              </div>
              <div class="bm-panel__stats">
                <span class="bm-stat">
                  <span>Voyage</span><b>{{ totalVoyageConsumption | number:'1.0-0' }}</b>
                </span>
                <span class="bm-stat">
                  <span>Benchmark</span><b>{{ totalBenchmarkConsumption | number:'1.0-0' }}</b>
                </span>
                <span class="bm-stat" [class.is-pos]="totalFuelSavingResult > 0"
                  [class.is-neg]="totalFuelSavingResult < 0">
                  <span>Saved</span><b>{{ totalFuelSavingResult | number:'1.0-0' }}</b>
                </span>
              </div>
            </header>
            <div class="bm-bars">
              <div [chart]="consumptionChart"></div>
            </div>
          </article>
        </ng-container>

        <div class="vs-no-rows" *ngIf="benchmarkData.length === 0">
          <span class="vs-no-rows__icon"><mat-icon>insights</mat-icon></span>
          <h3>No analytics data available</h3>
          <p>Adjust the client, vessel or date filters to view performance insights.</p>
          <button type="button" class="vs-btn" *ngIf="hasActiveFilters" (click)="clearClick()">
            <mat-icon>restart_alt</mat-icon>
            <span>Reset filters</span>
          </button>
        </div>
      </ng-container>
    </div>
  </section>

  <ng-template #bmDash><span class="vs-dash">—</span></ng-template>

  <!-- ======================= Operator remarks dialog ======================= -->
  <div class="vs-overlay" *ngIf="showRemarksPopup" (click)="closeOperatorRemarksPopup()">
    <div class="vs-dialog remarks-modal-dialog" role="dialog" aria-modal="true" aria-labelledby="bm-remarks-title"
      (click)="$event.stopPropagation()">
      <header class="vs-dialog__head">
        <span class="bm-dialog-icon" aria-hidden="true"><mat-icon>edit_note</mat-icon></span>
        <div class="vs-dialog__titles">
          <h3 id="bm-remarks-title">Operator remarks</h3>
          <div class="vs-dialog__chips">
            <span class="vs-chip"><mat-icon>directions_boat</mat-icon>{{ selectedRemarksRow?.vessel_name || '—'
              }}</span>
            <span class="vs-chip" *ngIf="selectedRemarksRow?.voy_no">Voy {{ selectedRemarksRow?.voy_no }}</span>
            <span class="vs-chip" *ngIf="selectedRemarksRow?.start_port || selectedRemarksRow?.end_port">
              <mat-icon>route</mat-icon>
              {{ selectedRemarksRow?.start_port || '—' }} &rarr; {{ selectedRemarksRow?.end_port || '—' }}
            </span>
          </div>
        </div>
        <button type="button" class="vs-icon-btn vs-icon-btn--ghost" (click)="closeOperatorRemarksPopup()"
          [disabled]="isSavingRemarks" aria-label="Close dialog" title="Close">
          <mat-icon>close</mat-icon>
        </button>
      </header>

      <div class="vs-dialog__body">
        <ol class="bm-points">
          <li *ngFor="let point of remarksPoints; let i = index; trackBy: trackRemarkPoint" class="bm-point">
            <span class="bm-point__index">{{ i + 1 }}</span>
            <textarea class="point-textarea bm-point__input" [(ngModel)]="point.text"
              (input)="autoGrowTextarea($event.target)" (focus)="autoGrowTextarea($event.target)"
              [readonly]="isClientLogin || isSavingRemarks"
              placeholder="Enter remark observation (e.g. Heating completed)…" rows="1"
              [attr.aria-label]="'Remark ' + (i + 1)"></textarea>
            <button type="button" class="vs-icon-btn vs-icon-btn--ghost bm-point__delete"
              *ngIf="remarksPoints.length > 1 && !isClientLogin" [disabled]="isSavingRemarks"
              (click)="removeRemarkPoint(i)" title="Remove this point" aria-label="Remove point">
              <mat-icon>delete_outline</mat-icon>
            </button>
          </li>
        </ol>

        <button type="button" class="bm-add-point" *ngIf="!isClientLogin" [disabled]="isSavingRemarks"
          (click)="addRemarkPoint()">
          <mat-icon>add</mat-icon>
          <span>Add remark</span>
        </button>
      </div>

      <footer class="vs-dialog__foot">
        <span class="bm-dialog-meta">{{ remarksPoints.length }} observation point{{ remarksPoints.length === 1 ? '' : 's'
          }}</span>
        <button type="button" class="vs-btn" (click)="closeOperatorRemarksPopup()" [disabled]="isSavingRemarks">
          Cancel
        </button>
        <button type="button" class="vs-btn vs-btn--primary" (click)="saveOperatorRemarks()"
          [disabled]="isSavingRemarks || isClientLogin">
          <mat-icon [class.vs-spin]="isSavingRemarks">{{ isSavingRemarks ? 'autorenew' : 'save' }}</mat-icon>
          <span>{{ isSavingRemarks ? 'Saving…' : 'Save remarks' }}</span>
        </button>
      </footer>
    </div>
  </div>
</div>
